import { z } from 'zod'

import type { FormField } from '@@types'

/**
 * Build Zod validation schema from profile fields metadata
 * Ported from mobile app for dynamic validation
 */
export const buildSchemaFromFields = (
  fields: FormField[],
  options?: {
    includeAcceptPolicy?: boolean
  },
) => {
  const includeAcceptPolicy = options?.includeAcceptPolicy ?? true
  const shape: Record<string, z.ZodTypeAny> = {}

  const filteredFields = fields.filter((field) => field?.fieldName !== 'children')

  for (const field of filteredFields) {
    if (!field?.fieldName) continue

    const fieldLabel = field.label || 'Поле'

    if (field.required) {
      switch (field.type) {
      case 'date':
        shape[field.fieldName] = z
          .string({ required_error: `${fieldLabel} обязательно для заполнения` })
          .min(1, `${fieldLabel} обязательно для заполнения`)
        break
      case 'email':
        shape[field.fieldName] = z
          .string({ required_error: `${fieldLabel} обязательно для заполнения` })
          .email('Введите корректный email')
          .min(1, `${fieldLabel} обязательно для заполнения`)
        break
      case 'string':
        shape[field.fieldName] = z
          .string({ required_error: `${fieldLabel} обязательно для заполнения` })
          .min(1, `${fieldLabel} обязательно для заполнения`)
        break
      case 'select':
        // Use coerce to accept both string and number values from server
        shape[field.fieldName] = z.coerce
          .string({ required_error: `${fieldLabel} обязательно для заполнения` })
          .min(1, `${fieldLabel} обязательно для заполнения`)
        break
      case 'boolean':
        shape[field.fieldName] = z.boolean({
          required_error: `${fieldLabel} обязательно для заполнения`,
        })
        break
      case 'number':
        // Use coerce to accept string input from form
        shape[field.fieldName] = z.coerce
          .number({ 
            required_error: `${fieldLabel} обязательно для заполнения`,
            invalid_type_error: `${fieldLabel} должно быть числом`,
          })
          .min(0, `${fieldLabel} должно быть положительным числом`)
        break
      default:
        shape[field.fieldName] = z.any()
      }
    }
    else if (field.type === 'email') {
      // Optional email should still be valid if provided
      shape[field.fieldName] = z.union([
        z.literal(''),
        z.string().email('Введите корректный email'),
      ])
    }
    else if (field.type === 'select') {
      // Optional select can be string or number
      shape[field.fieldName] = z.coerce.string().optional().or(z.literal(''))
    }
    else if (field.type === 'number') {
      // Optional number
      shape[field.fieldName] = z.coerce.number().optional().or(z.literal(''))
    }
    else {
      shape[field.fieldName] = z.any().optional()
    }
  }

  // Wrap in profileInfo structure to match form structure
  return z.object({
    profileInfo: z.object(shape),
    ...(includeAcceptPolicy
      ? {
        acceptPolicy: z.boolean().refine((val) => val === true, {
          message: 'Необходимо согласие с политикой конфиденциальности',
        }),
      }
      : {}),
  })
}

/**
 * Group profile fields by group property
 * Normalize fieldName by replacing dots with double underscores for form compatibility
 */
export const groupProfileFields = (
  fields: FormField[] | null | undefined,
  isRegistration?: boolean,
): {
  fieldsByGroup: Record<string, FormField[]>
  normalizedFields: FormField[]
} => {
  if (!fields) {
    return { fieldsByGroup: {}, normalizedFields: [] }
  }

  // Filter fields based on visibility
  const filteredFields = fields.filter((field) => {
    if (isRegistration) {
      return field?.isVisibleInRegistration
    }
    return field?.isVisibleInProfile !== false
  })

  // Normalize fieldName and parse payload
  const normalizedFields: FormField[] = filteredFields.map((field) => {
    const fieldName = field?.fieldName?.split('.').join('__') || ''
    
    let payload = field?.payload
    if (typeof payload === 'string') {
      try {
        payload = JSON.parse(payload)
      }
      catch {
        payload = {}
      }
    }

    return {
      ...field,
      payload,
      fieldName,
    }
  })

  // Group by group property
  const fieldsByGroup = normalizedFields.reduce((acc, field) => {
    const group = field?.group || 'default'
    if (!acc[group]) {
      acc[group] = []
    }
    acc[group].push(field)
    return acc
  }, {} as Record<string, FormField[]>)

  return {
    fieldsByGroup,
    normalizedFields,
  }
}
